/** @format */

import { useTranslation } from "react-i18next";
import {
	QUESTION_ASSET_WIDTHS,
	type Brand,
	type QuestionAssetWidth,
	type QuestionContentBlock,
} from "@question-bank/shared";
import { ImageUpload } from "./ImageUpload";
import { MathTextField } from "./MathTextField";

interface ReviewQuestionBlocksProps {
	brand: Brand;
	blocks: QuestionContentBlock[];
	onChange: (blocks: QuestionContentBlock[]) => void;
}

const fieldClass = "mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800";

const replaceBlock = (
	blocks: QuestionContentBlock[],
	index: number,
	block: QuestionContentBlock,
): QuestionContentBlock[] => blocks.map((item, itemIndex) => (itemIndex === index ? block : item));

/**
 * The question-edit content blocks, kept to the review column.
 * Text uses the same KaTeX field as the edit modal. Images use the same upload.
 */
export const ReviewQuestionBlocks = ({ brand, blocks, onChange }: ReviewQuestionBlocksProps) => {
	const { t } = useTranslation();
	const isWelshBrand = brand === "wjec";

	const moveBlock = (index: number, direction: -1 | 1) => {
		const next = index + direction;
		if (next < 0 || next >= blocks.length) {
			return;
		}
		const copy = [...blocks];
		const [moved] = copy.splice(index, 1);
		copy.splice(next, 0, moved);
		onChange(copy);
	};

	const blockLabel = (block: QuestionContentBlock): string =>
		block.type === "asset"
			? t("questionForm.imageBlockLabel")
			: block.type === "answerLines"
				? t("questionForm.answerLinesBlockLabel")
				: t("questionForm.textBlockLabel");

	return (
		<div className="space-y-3">
			{blocks.map((block, index) => (
				<div key={`${block.type}-${index}`} className="rounded-md border border-slate-200 p-3">
					<div className="mb-2 flex items-center justify-between gap-2">
						<span className="text-sm font-semibold text-slate-700">{`${index + 1}. ${blockLabel(block)}`}</span>
						<div className="flex gap-1">
							<button
								type="button"
								aria-label={t("questionForm.moveBlockUp")}
								disabled={index === 0}
								onClick={() => moveBlock(index, -1)}
								className="rounded-md border border-slate-200 px-2 py-1 text-sm disabled:opacity-40"
							>
								↑
							</button>
							<button
								type="button"
								aria-label={t("questionForm.moveBlockDown")}
								disabled={index === blocks.length - 1}
								onClick={() => moveBlock(index, 1)}
								className="rounded-md border border-slate-200 px-2 py-1 text-sm disabled:opacity-40"
							>
								↓
							</button>
							<button
								type="button"
								onClick={() => onChange(blocks.filter((_, itemIndex) => itemIndex !== index))}
								className="rounded-md border border-red-300 px-2 py-1 text-sm font-semibold text-red-600"
							>
								{t("questionForm.removeBlock")}
							</button>
						</div>
					</div>

					{block.type === "text" && (
						<div className="space-y-3">
							<MathTextField
								id={`review-block-${index}-en`}
								label={t("questionForm.questionContentEnLabel")}
								value={block.en ?? ""}
								onValueChange={(value) => onChange(replaceBlock(blocks, index, { ...block, en: value }))}
								textareaClassName={fieldClass}
								rows={4}
							/>
							{isWelshBrand && (
								<MathTextField
									id={`review-block-${index}-cy`}
									label={t("questionForm.questionContentCyLabel")}
									value={block.cy ?? ""}
									onValueChange={(value) => onChange(replaceBlock(blocks, index, { ...block, cy: value }))}
									textareaClassName={fieldClass}
									rows={4}
								/>
							)}
						</div>
					)}

					{block.type === "asset" && (
						<div className="space-y-3">
							<ImageUpload
								label={t("questionForm.imageLanguageEnglish")}
								brand={brand}
								imageInputId={`review-block-${index}-asset-en`}
								existingAssetKey={block.en || undefined}
								onImageUpload={(assetKey) => onChange(replaceBlock(blocks, index, { ...block, en: assetKey }))}
								onImageRemove={() => onChange(replaceBlock(blocks, index, { ...block, en: "" }))}
							/>
							{isWelshBrand && (
								<ImageUpload
									label={t("questionForm.imageLanguageWelsh")}
									brand={brand}
									imageInputId={`review-block-${index}-asset-cy`}
									existingAssetKey={block.cy || undefined}
									onImageUpload={(assetKey) => onChange(replaceBlock(blocks, index, { ...block, cy: assetKey }))}
									onImageRemove={() => onChange(replaceBlock(blocks, index, { ...block, cy: "" }))}
								/>
							)}
							{(block.enRaster || block.cyRaster) && (
								<div className="rounded-md border border-slate-200 bg-slate-50 p-3">
									<p className="text-sm font-medium text-slate-700">{t("questionForm.assetBackupLabel")}</p>
									<p className="mt-1 text-sm text-slate-600">{t("questionForm.assetBackupHint")}</p>
									<button
										type="button"
										className="mt-2 rounded-md border border-slate-300 bg-white px-3 py-1 text-sm font-semibold text-slate-800"
										onClick={() =>
											onChange(
												replaceBlock(blocks, index, {
													...block,
													...(block.enRaster ? { en: block.enRaster } : {}),
													...(block.cyRaster ? { cy: block.cyRaster } : {}),
												}),
											)
										}
									>
										{t("questionForm.assetUseBackup")}
									</button>
								</div>
							)}
							<label className="block text-sm font-medium text-slate-700" htmlFor={`review-block-${index}-width`}>
								{t("questionForm.imageBlockWidthLabel")}
								<select
									id={`review-block-${index}-width`}
									className={fieldClass}
									value={block.width ?? ""}
									onChange={(event) => {
										const width =
											event.target.value === "" ? undefined : (Number(event.target.value) as QuestionAssetWidth);
										onChange(replaceBlock(blocks, index, { ...block, width }));
									}}
								>
									<option value="">{t("questionForm.imageBlockWidthDefaultOption")}</option>
									{QUESTION_ASSET_WIDTHS.map((width) => (
										<option key={width} value={width}>{`${width}%`}</option>
									))}
								</select>
							</label>
						</div>
					)}

					{block.type === "answerLines" && (
						<label className="block text-sm font-medium text-slate-700" htmlFor={`review-block-${index}-lines`}>
							{t("questionForm.answerLinesBlockFieldLabel")}
							<input
								id={`review-block-${index}-lines`}
								type="number"
								min={1}
								max={60}
								className={`${fieldClass} w-24`}
								value={block.lines}
								onChange={(event) =>
									onChange(replaceBlock(blocks, index, { ...block, lines: Number(event.target.value) }))
								}
							/>
						</label>
					)}
				</div>
			))}

			<div className="flex flex-wrap gap-2">
				<button
					type="button"
					onClick={() => onChange([...blocks, { type: "text", en: "", cy: "" }])}
					className="rounded-md border border-(--admin-color-primary-strong) px-3 py-1.5 text-sm font-semibold text-(--admin-color-primary-strong)"
				>
					{t("questionForm.addTextBlock")}
				</button>
				<button
					type="button"
					onClick={() => onChange([...blocks, { type: "asset", en: "", cy: "" }])}
					className="rounded-md border border-(--admin-color-primary-strong) px-3 py-1.5 text-sm font-semibold text-(--admin-color-primary-strong)"
				>
					{t("questionForm.addImageBlock")}
				</button>
				<button
					type="button"
					onClick={() => onChange([...blocks, { type: "answerLines", lines: 4 }])}
					className="rounded-md border border-(--admin-color-primary-strong) px-3 py-1.5 text-sm font-semibold text-(--admin-color-primary-strong)"
				>
					{t("questionForm.addAnswerLinesBlock")}
				</button>
			</div>
		</div>
	);
};
